import { formatBackupDate } from "../../util/backup.date";
import BackupDetailsModal from "./backup.details.modal";
import * as Tooltip from "@radix-ui/react-tooltip";
import { MouseEvent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DiskBackupDateContentsResponse,
  fetchDeleteByBackupDate,
  fetchDiskBackupDateContents,
} from "../../api/admin";
import { useAuthStore } from "../../atoms/user.auth.store";
import ContentsTopHeader from "../common/contents.top.header";
import ModalCenterLayout from "../modal/modal.center.layout";
import {
  DeviceDeleteTooltip,
  BackupDateContainer,
  BackupDateContentsBody,
} from "../../style/contents/backup/backup";

type DeleteTarget = {
  rootDirectoryPath: string;
  backupDate: string;
};

type DeleteResult = {
  files: number;
  directories: number;
  histories: number;
};

const getUserAccount = () => {
  const userInfo = localStorage.getItem("userInfo");
  if (!userInfo) return "";

  try {
    const parsed = JSON.parse(userInfo);
    return parsed.userAccount || "";
  } catch {
    return "";
  }
};

const getBackupTypeLabel = (backupType?: string) => {
  if (backupType === "full") return "전체 백업";
  if (backupType === "incremental") return "증분 백업";

  return backupType || "-";
};

export interface BackupDateContext {
  deviceId: string;
  userId: number;
  userAccount: string;
  rootDirectoryPath: string;
  pcName: string;
}

const BackupDateList = ({ context }: { context?: BackupDateContext }) => {
  const { role } = useAuthStore();
  const [detailsTarget, setDetailsTarget] = useState<{
    rootDirectoryPath: string;
    backupDate: string;
  } | null>(null);
  const [data, setData] = useState<DiskBackupDateContentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isVisible: false,
  });
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);
  const [deleteResultModal, setDeleteResultModal] = useState({
    isOpen: false,
    isVisible: false,
  });

  const [loadError, setLoadError] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { path: rawDeviceId = "" } = useParams<{ path: string }>();
  const deviceId = context?.deviceId ?? decodeURIComponent(rawDeviceId);
  const queryParams = new URLSearchParams(location.search);
  const userId = context ? String(context.userId) : queryParams.get("id");
  const queryUserAccount =
    context?.userAccount ?? queryParams.get("userAccount");
  const rootDirectoryPath =
    context?.rootDirectoryPath ?? queryParams.get("rootDirectoryPath");
  const userAccount =
    role === "user" ? getUserAccount() || queryUserAccount : queryUserAccount;
  const isAdmin = role === "admin";
  const filteredRootBackupDates =
    data?.rootBackupDates.filter(
      (item) => item.rootDirectoryPath === rootDirectoryPath,
    ) || [];

  const handleSearch = () => {
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = () => {};

  const handleBackupDateClick = (
    rootDirectoryPath: string,
    backupDate: string,
  ) => {
    const encodedRootDirectoryPath = encodeURIComponent(rootDirectoryPath);
    const nextQueryParams = new URLSearchParams(
      context ? undefined : location.search,
    );
    if (context) {
      nextQueryParams.set("id", String(context.userId));
      nextQueryParams.set("userAccount", context.userAccount);
      nextQueryParams.set("rootDirectoryPath", rootDirectoryPath);
      nextQueryParams.set(
        "pcName",
        btoa(encodeURIComponent(context.pcName || "")),
      );
    }

    nextQueryParams.set("deviceId", deviceId);
    nextQueryParams.set("backupDate", backupDate);

    localStorage.setItem(
      "directory",
      JSON.stringify({
        directoryPath: rootDirectoryPath,
        createDate: backupDate,
      }),
    );
    localStorage.setItem("initialPath", rootDirectoryPath);

    navigate(
      `/backup/file/list/${encodedRootDirectoryPath}?${nextQueryParams.toString()}`,
    );
  };

  const loadBackupDateContents = useCallback(async () => {
    if (!role) return;
    if (!deviceId || !userId || !userAccount) return;

    try {
      setIsLoading(true);
      setLoadError(false);
      const response = await fetchDiskBackupDateContents({
        deviceId,
        userId: Number(userId),
        userAccount,
        isAdmin,
      });
      setData(response);
    } catch {
      setLoadError(true);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, isAdmin, role, userAccount, userId]);

  const handleOpenDeleteModal = (
    event: MouseEvent<HTMLButtonElement>,
    rootDirectoryPath: string,
    backupDate: string,
  ) => {
    event.stopPropagation();
    setDeleteTarget({ rootDirectoryPath, backupDate });
    setDeleteModal({ isOpen: true, isVisible: true });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ isOpen: false, isVisible: false });
    setDeleteTarget(null);
  };

  const handleCloseDeleteResultModal = () => {
    setDeleteResultModal({ isOpen: false, isVisible: false });
    setDeleteResult(null);
  };

  const handleDeleteBackupDate = async () => {
    const adminUserId = localStorage.getItem("userId");

    if (!adminUserId) {
      alert("관리자 정보가 없습니다.");
      return;
    }

    if (!deleteTarget) {
      alert("삭제할 백업 날짜 정보가 없습니다.");
      return;
    }

    try {
      const response = await fetchDeleteByBackupDate(adminUserId, {
        backupDate: deleteTarget.backupDate,
        rootDirectoryPath: deleteTarget.rootDirectoryPath,
      });
      console.log("deleteTarget", deleteTarget);
      console.log("response:", response);

      handleCloseDeleteModal();
      setDeleteResult(response.message);
      setDeleteResultModal({ isOpen: true, isVisible: true });
      loadBackupDateContents();
    } catch (err) {
      console.error("백업 날짜 삭제 실패:", err);
      alert("백업 날짜 삭제 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    loadBackupDateContents();
  }, [loadBackupDateContents]);

  return (
    <Tooltip.Provider delayDuration={200}>
      <BackupDateContainer style={context ? { height: "auto" } : undefined}>
        {!context && (
          <ContentsTopHeader
            title="백업 날짜 선택"
            onSearch={handleSearch}
            onPageSizeChange={handlePageSizeChange}
            pageSizeHidden={true}
            backArrowPath="-1"
          />
        )}
        <BackupDateContentsBody
          style={context ? { height: "auto", maxHeight: "65vh" } : undefined}
        >
          {isLoading ? (
            <p>불러오는 중입니다.</p>
          ) : loadError ? (
            <div role="alert">
              백업 목록을 불러오지 못했습니다.{" "}
              <button type="button" onClick={loadBackupDateContents}>
                다시 시도
              </button>
            </div>
          ) : filteredRootBackupDates.length === 0 ? (
            <p className="no-data">백업 날짜가 없습니다.</p>
          ) : (
            <div className="backup-date-table-wrap">
              <table className="backup-date-table">
                <thead>
                  <tr>
                    <th scope="col">날짜</th>
                    <th scope="col">백업된 파일 수</th>
                    <th scope="col">백업 형태</th>
                    <th scope="col">동작</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRootBackupDates.flatMap((rootBackupDate) =>
                    rootBackupDate.backupDates.map((backupDate) => (
                      <tr
                        key={`${rootBackupDate.rootDirectoryPath}-${backupDate.backupDate}`}
                      >
                        <td>
                          <button
                            type="button"
                            className="backup-date-open-btn"
                            onClick={() =>
                              handleBackupDateClick(
                                rootBackupDate.rootDirectoryPath,
                                backupDate.backupDate,
                              )
                            }
                          >
                            {formatBackupDate(backupDate.backupDate)}
                          </button>
                        </td>
                        <td>{backupDate.backupHistoryCount}개</td>
                        <td>{getBackupTypeLabel(backupDate.backupType)}</td>
                        <td>
                          <div className="cell-btn_wrap">
                            {isAdmin ? (
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <button
                                    aria-label="삭제"
                                    type="button"
                                    className="backup-date-delete-btn"
                                    onClick={(event) =>
                                      handleOpenDeleteModal(
                                        event,
                                        rootBackupDate.rootDirectoryPath,
                                        backupDate.backupDate,
                                      )
                                    }
                                  >
                                    <img
                                      src="/assets/new_design/del_icon.png"
                                      alt=""
                                    />
                                  </button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <DeviceDeleteTooltip
                                    side="top"
                                    sideOffset={6}
                                    collisionPadding={8}
                                  >
                                    삭제
                                    <Tooltip.Arrow fill="#333333" />
                                  </DeviceDeleteTooltip>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            ) : null}
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <button
                                  type="button"
                                  className="view-btn-open"
                                  aria-label="파일보기"
                                  onClick={() =>
                                    setDetailsTarget({
                                      rootDirectoryPath:
                                        rootBackupDate.rootDirectoryPath,
                                      backupDate: backupDate.backupDate,
                                    })
                                  }
                                >
                                  <img
                                    src="/assets/new_design/view_icon.png"
                                    alt=""
                                  />
                                </button>
                              </Tooltip.Trigger>
                              <Tooltip.Portal>
                                <DeviceDeleteTooltip
                                  side="top"
                                  sideOffset={6}
                                  collisionPadding={8}
                                >
                                  파일보기
                                  <Tooltip.Arrow fill="#333333" />
                                </DeviceDeleteTooltip>
                              </Tooltip.Portal>
                            </Tooltip.Root>
                          </div>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </BackupDateContentsBody>
        {deleteModal.isOpen && (
          <ModalCenterLayout
            isVisible={deleteModal.isVisible}
            closedHandler={handleCloseDeleteModal}
            size="s"
          >
            <div className="backup-date-delete-modal">
              <div className="modal-header">
                <h3>백업 날짜 삭제</h3>
                <button
                  type="button"
                  className="closed-modal-btn"
                  onClick={handleCloseDeleteModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>해당 백업 날짜를 삭제하시겠습니까?</p>
                {deleteTarget && (
                  <p className="delete-target">
                    {formatBackupDate(deleteTarget.backupDate)}
                  </p>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel"
                  onClick={handleCloseDeleteModal}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="confirm"
                  onClick={handleDeleteBackupDate}
                >
                  삭제
                </button>
              </div>
            </div>
          </ModalCenterLayout>
        )}
        {deleteResultModal.isOpen && (
          <ModalCenterLayout
            isVisible={deleteResultModal.isVisible}
            closedHandler={handleCloseDeleteResultModal}
            size="s"
          >
            <div className="backup-date-delete-modal">
              <div className="modal-header">
                <h3>삭제 완료</h3>
                <button
                  type="button"
                  className="closed-modal-btn"
                  onClick={handleCloseDeleteResultModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>삭제되었습니다.</p>
                {deleteResult && (
                  <div className="delete-result">
                    <span>파일: {deleteResult.files}개</span>
                    <span>폴더: {deleteResult.directories}개</span>
                    <span>이력: {deleteResult.histories}개</span>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="confirm_"
                  onClick={handleCloseDeleteResultModal}
                >
                  확인
                </button>
              </div>
            </div>
          </ModalCenterLayout>
        )}
        {detailsTarget && userId && deviceId && userAccount && (
          <BackupDetailsModal
            context={{
              deviceId,
              userId: Number(userId),
              userAccount,
              rootDirectoryPath: detailsTarget.rootDirectoryPath,
              pcName: context?.pcName ?? "",
            }}
            backupDate={detailsTarget.backupDate}
            role={role || ""}
            onClose={() => setDetailsTarget(null)}
          />
        )}
      </BackupDateContainer>
    </Tooltip.Provider>
  );
};

export default BackupDateList;
